import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  ArrowUp,
  CalendarDays,
  ChevronDown,
  Headphones,
  Mic,
  Smile,
  Settings,
  SlidersHorizontal,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './AstroFirstTimeDemo.css';

type DemoPhase =
  | 'typing'
  | 'pauseAfterTyping'
  | 'backspacing'
  | 'pauseBeforeNext'
  | 'submit'
  | 'answering'
  | 'complete';

interface DemoStep {
  id: number;
  question: string;
}

export interface AstroFirstTimeDemoProps {
  userName?: string;
  birthDate?: string;
  birthTime?: string;
  tokensRemainingLabel?: string;
  questions?: string[];
  hint?: string;
  callout?: string;
  placeholder?: string;
  answeringText?: string;
  freePlanLabel?: string;
  upgradeLabel?: string;
  guidanceLabel?: string;
  horoscopeReadyTitle?: string;
  horoscopeReadyLead?: string;
  dayQualityTitle?: string;
  dayQualitySubtitle?: string;
  dayQualityMethod?: string;
  dayQualitySunriseSunset?: string;
  dateLabel?: string;
  dateValue?: string;
  favorableLabel?: string;
  favorableWindow?: string;
  nameLabel?: string;
  birthDateLabel?: string;
  birthTimeLabel?: string;
  closeDemoLabel?: string;
  demoLabel?: string;
  demoLabelMobile?: string;
  modeBasicLabel?: string;
  modeAdvancedLabel?: string;
  nowLabel?: string;
  abhijitMuhurtaLabel?: string;
  godhuliMuhurtaLabel?: string;
  brahmaMuhurtaLabel?: string;
  nextDayLabel?: string;
  favorableLegendLabel?: string;
  cautionLabel?: string;
  neutralLabel?: string;
  avoidLabel?: string;
  rahuKalaEtcLabel?: string;
  mobileCoachTitle?: string;
  mobileCoachText?: string;
  mobileCoachDismissLabel?: string;
  heritageTabLabel?: string;
  astroTabLabel?: string;
  tabHighlightLabel?: string;
  loop?: boolean;
  onComplete?: () => void;
  onDismiss?: () => void;
}

const DEFAULT_QUESTIONS = [
  'What does my chart indicate about career growth this year?',
  'When is the next strong period for marriage or relationship progress?',
  'Which Mahadasha am I currently running?',
];

const TYPE_SPEED = 34;
const BACKSPACE_SPEED = 18;
const HOLD_AFTER_TYPE = 800;
const HOLD_BEFORE_NEXT = 320;
const SUBMIT_DELAY = 500;
const ANSWERING_DURATION = 2500;
const REDUCED_MOTION_DURATION = 4200;

const normalizeQuestions = (questions?: string[]): DemoStep[] => {
  const usable = (questions && questions.length > 0 ? questions : DEFAULT_QUESTIONS)
    .filter((question) => typeof question === 'string' && question.trim().length > 0)
    .slice(0, 3);

  const padded = usable.length >= 3 ? usable : [...usable, ...DEFAULT_QUESTIONS].slice(0, 3);
  return padded.map((question, index) => ({ id: index + 1, question }));
};

function useDemoSequence(steps: DemoStep[], loop: boolean, onComplete?: () => void) {
  const [stepIndex, setStepIndex] = useState(0);
  const [displayValue, setDisplayValue] = useState('');
  const [phase, setPhase] = useState<DemoPhase>('typing');
  const [answering, setAnswering] = useState(false);
  const reduceMotion = useReducedMotion();

  const currentStep = steps[stepIndex] ?? steps[0];
  const isFinalStep = stepIndex === steps.length - 1;

  useEffect(() => {
    if (!reduceMotion) return undefined;

    setStepIndex(steps.length - 1);
    setDisplayValue(steps[steps.length - 1]?.question ?? '');
    setAnswering(true);
    setPhase('answering');

    const timer = window.setTimeout(() => {
      setAnswering(false);
      setPhase('complete');
      onComplete?.();
    }, REDUCED_MOTION_DURATION);

    return () => window.clearTimeout(timer);
  }, [onComplete, reduceMotion, steps]);

  useEffect(() => {
    if (reduceMotion || phase === 'complete') return undefined;

    let timer: number | undefined;

    if (phase === 'typing') {
      if (displayValue.length < currentStep.question.length) {
        timer = window.setTimeout(() => {
          setDisplayValue(currentStep.question.slice(0, displayValue.length + 1));
        }, TYPE_SPEED);
      } else {
        timer = window.setTimeout(() => {
          setPhase(isFinalStep ? 'submit' : 'pauseAfterTyping');
        }, HOLD_AFTER_TYPE);
      }
    }

    if (phase === 'pauseAfterTyping') {
      timer = window.setTimeout(() => setPhase('backspacing'), HOLD_AFTER_TYPE);
    }

    if (phase === 'backspacing') {
      if (displayValue.length > 0) {
        timer = window.setTimeout(() => {
          setDisplayValue((prev) => prev.slice(0, -1));
        }, BACKSPACE_SPEED);
      } else {
        setPhase('pauseBeforeNext');
      }
    }

    if (phase === 'pauseBeforeNext') {
      timer = window.setTimeout(() => {
        setStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
        setPhase('typing');
      }, HOLD_BEFORE_NEXT);
    }

    if (phase === 'submit') {
      timer = window.setTimeout(() => {
        setAnswering(true);
        setPhase('answering');
      }, SUBMIT_DELAY);
    }

    if (phase === 'answering') {
      timer = window.setTimeout(() => {
        setAnswering(false);

        if (loop) {
          setStepIndex(0);
          setDisplayValue('');
          setPhase('typing');
          return;
        }

        setPhase('complete');
        onComplete?.();
      }, ANSWERING_DURATION);
    }

    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [currentStep.question, displayValue, isFinalStep, loop, onComplete, phase, reduceMotion, steps.length]);

  return {
    answering,
    displayValue,
    phase,
    stepIndex,
  };
}

function Cursor() {
  return <span className="aftd-cursor">|</span>;
}

function FloatingQuestionList({
  phase,
  stepIndex,
  steps,
}: {
  phase: DemoPhase;
  stepIndex: number;
  steps: DemoStep[];
}) {
  const previews = useMemo(
    () =>
      steps.map((step, index) => ({
        ...step,
        active: index === stepIndex,
        done: index < stepIndex,
        backspacing: index === stepIndex && phase === 'backspacing',
      })),
    [phase, stepIndex, steps],
  );

  return (
    <div className="aftd-floating-steps" aria-hidden="true">
      {previews.map((item, index) => (
        <motion.div
          key={item.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28 }}
          className={[
            'aftd-floating-step',
            item.active ? 'aftd-floating-step-active' : '',
            item.done ? 'aftd-floating-step-done' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className="aftd-step-badge">{item.id}</div>
          <div className="aftd-step-content">
            <span>{item.question}</span>
            {item.active && phase !== 'answering' && phase !== 'complete' && <Cursor />}
          </div>
          {index < previews.length - 1 && <div className="aftd-step-connector" />}
        </motion.div>
      ))}
    </div>
  );
}

function AnsweringPill({ answeringText }: { answeringText: string }) {
  return (
    <div className="aftd-answering-pill">
      <span>{answeringText}</span>
      <span className="aftd-answering-dots" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
    </div>
  );
}

export default function AstroFirstTimeDemo({
  userName = 'Maya',
  birthDate = '1986-05-11',
  birthTime = '20:50',
  tokensRemainingLabel = '6,000 tokens remaining',
  questions,
  hint = 'Watch how to ask your chart',
  callout = 'Ask personalized questions',
  placeholder = 'Ask about your chart...',
  answeringText = 'Reading your Vedic chart...',
  freePlanLabel = 'Free Plan',
  upgradeLabel = 'Upgrade',
  guidanceLabel = 'Guidance',
  horoscopeReadyTitle = 'Your horoscope is ready.',
  horoscopeReadyLead = 'Type a question below or choose a suggested question to begin.',
  dayQualityTitle = 'Personalized Day Quality',
  dayQualitySubtitle = 'Your best hours to act today',
  dayQualityMethod = '8 factors - Personalised to your birth Nakshatra',
  dayQualitySunriseSunset = 'Sunrise 05:55:07 - Sunset 18:32:10',
  dateLabel = 'WEDNESDAY',
  dateValue = '29 April 2026',
  favorableLabel = 'FAVORABLE',
  favorableWindow = '10:39 AM-12:13 PM',
  nameLabel = 'Name',
  birthDateLabel = 'Birth Date',
  birthTimeLabel = 'Time',
  closeDemoLabel = 'Close demo',
  demoLabel = 'Demo · auto-closes in a few seconds · press Esc or click anywhere to skip',
  demoLabelMobile = 'Demo · tap anywhere to skip',
  modeBasicLabel = 'Basic',
  modeAdvancedLabel = 'Advanced',
  nowLabel = 'NOW',
  abhijitMuhurtaLabel = 'Abhijit Muhurta',
  godhuliMuhurtaLabel = 'Godhuli Muhurta',
  brahmaMuhurtaLabel = 'Brahma Muhurta',
  nextDayLabel = 'next day',
  favorableLegendLabel = 'Favorable',
  cautionLabel = 'Favorable with caution',
  neutralLabel = 'Neutral',
  avoidLabel = 'Avoid',
  rahuKalaEtcLabel = 'Rahu Kala etc.',
  mobileCoachTitle = 'Ask your chart here',
  mobileCoachText = 'Career, love, timing, remedies, or your daily routine — type your question in the box below.',
  mobileCoachDismissLabel = 'Tap anywhere to continue',
  heritageTabLabel = 'Heritage',
  astroTabLabel = 'Astro',
  tabHighlightLabel = 'Switch to Heritage for general text questions, then come back to your chart any time.',
  loop = false,
  onComplete,
  onDismiss,
}: AstroFirstTimeDemoProps) {
  const steps = useMemo(() => normalizeQuestions(questions), [questions]);
  const { answering, displayValue, phase, stepIndex } = useDemoSequence(steps, loop, onComplete);
  const pointerStartRef = useRef<{ x: number; y: number; time: number; pointerType: string } | null>(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss?.();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onDismiss]);

  const handleBackdropPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    pointerStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      time: window.performance.now(),
      pointerType: event.pointerType,
    };
  };

  const handleBackdropPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const started = pointerStartRef.current;
    const moved = started
      ? Math.hypot(event.clientX - started.x, event.clientY - started.y)
      : 0;
    const elapsed = started ? window.performance.now() - started.time : 0;
    const isDeliberateClick = moved < 12 && elapsed < 800;

    if (event.currentTarget === event.target || isDeliberateClick) {
      onDismiss?.();
    }
  };

  return (
    <div
      className="aftd-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={hint}
      onPointerDown={handleBackdropPointerDown}
      onPointerUp={handleBackdropPointerUp}
    >
      <button type="button" className="aftd-close" onClick={onDismiss} aria-label={closeDemoLabel}>
        <X size={20} aria-hidden="true" />
      </button>

      <div className="aftd-mobile-coach">
        <div className="aftd-mobile-coach-chip">
          <div className="aftd-mobile-coach-kicker">
            <Sparkles size={13} aria-hidden="true" />
            {hint}
          </div>
          <strong>{mobileCoachTitle}</strong>
          <span>{mobileCoachText}</span>
        </div>
        <div className="aftd-mobile-composer-ring" aria-hidden="true" />
        <div className="aftd-mobile-coach-dismiss">{mobileCoachDismissLabel}</div>
      </div>

      <div className="aftd-stage">
        <div className="aftd-demo-pill" aria-live="polite">
          <span className="aftd-demo-label-full">{demoLabel}</span>
          <span className="aftd-demo-label-mobile">{demoLabelMobile}</span>
        </div>

        <div className="aftd-page">
          <div className="aftd-ornament aftd-ornament-left" aria-hidden="true" />
          <div className="aftd-ornament aftd-ornament-right" aria-hidden="true" />

          <div className="aftd-top-controls" aria-hidden="true">
            <div className="aftd-mode-pill aftd-mode-pill-active">{modeBasicLabel}</div>
            <div className="aftd-mode-pill">{modeAdvancedLabel}</div>
          </div>

          <div className="aftd-chat-mode-callout" aria-hidden="true">
            <div className="aftd-chat-mode-pill">
              <span>{heritageTabLabel}</span>
              <strong>{astroTabLabel}</strong>
            </div>
            <p>{tabHighlightLabel}</p>
          </div>

          <div className="aftd-day-card">
            <div className="aftd-day-header">
              <div>
                <h3>
                  <Sparkles size={16} aria-hidden="true" />
                  {dayQualityTitle}
                </h3>
                <p className="aftd-day-benefit">{dayQualitySubtitle}</p>
                <p className="aftd-day-method">{dayQualityMethod}</p>
                <p>{dayQualitySunriseSunset}</p>
              </div>
              <div className="aftd-date-card">
                <CalendarDays size={18} aria-hidden="true" />
                <span>{dateLabel}</span>
                <strong>{dateValue}</strong>
                <small>
                  {favorableLabel} - {favorableWindow}
                </small>
              </div>
            </div>

            <div className="aftd-quality-wrap" aria-hidden="true">
              <div className="aftd-quality-now">{nowLabel}</div>
              <div className="aftd-quality-bar">
                <span className="aftd-q-green" />
                <span className="aftd-q-striped" />
                <span className="aftd-q-red" />
                <span className="aftd-q-lime" />
                <span className="aftd-q-red aftd-striped-dark" />
                <span className="aftd-q-yellow" />
                <span className="aftd-q-green" />
                <span className="aftd-q-yellow" />
                <span className="aftd-q-red" />
              </div>
              <div className="aftd-quality-tags">
                <span className="aftd-tag aftd-tag-left">{abhijitMuhurtaLabel}</span>
                <span className="aftd-tag aftd-tag-middle">{godhuliMuhurtaLabel}</span>
                <span className="aftd-tag aftd-tag-right">{brahmaMuhurtaLabel}</span>
              </div>
              <div className="aftd-quality-times">
                <span>5:55 AM</span>
                <span>11:55 AM</span>
                <span>5:55 PM</span>
                <span>11:55 PM</span>
                <span>5:55 AM ({nextDayLabel})</span>
              </div>
              <div className="aftd-legend">
                <span><i className="aftd-legend-dot aftd-favorable" />{favorableLegendLabel}</span>
                <span><i className="aftd-legend-dot aftd-caution" />{cautionLabel}</span>
                <span><i className="aftd-legend-dot aftd-neutral" />{neutralLabel}</span>
                <span><i className="aftd-legend-dot aftd-avoid" />{avoidLabel}</span>
                <span><i className="aftd-legend-dot aftd-striped" />{rahuKalaEtcLabel}</span>
              </div>
            </div>
          </div>

          <div className="aftd-copy">
            <h2>{horoscopeReadyTitle}</h2>
            <h3>{horoscopeReadyLead}</h3>
            <p>{nameLabel}: {userName}</p>
            <p>{birthDateLabel}: {birthDate}</p>
            <p>{birthTimeLabel}: {birthTime}</p>
          </div>

          <div className="aftd-spotlight" aria-hidden="true" />

          <div className="aftd-focus-zone">
            <div className="aftd-hint">
              <Sparkles size={15} aria-hidden="true" />
              {hint}
            </div>

            <FloatingQuestionList stepIndex={stepIndex} phase={phase} steps={steps} />

            <div className="aftd-callout" aria-hidden="true">
              <span>{callout}</span>
            </div>

            <motion.div
              className="aftd-composer"
              animate={{
                boxShadow: [
                  '0 0 0 rgba(124, 77, 255, 0.18)',
                  '0 0 54px rgba(124, 77, 255, 0.42)',
                  '0 0 0 rgba(124, 77, 255, 0.18)',
                ],
              }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <div className="aftd-plan-banner">
                <div>
                  <strong>
                    <Sparkles size={12} aria-hidden="true" />
                    {freePlanLabel}
                  </strong>
                  <span>- {tokensRemainingLabel}</span>
                </div>
                <span className="aftd-upgrade">{upgradeLabel}</span>
              </div>

              <div className="aftd-profile-row">
                <span>{userName}</span>
                <ChevronDown size={16} aria-hidden="true" />
              </div>

              <div className="aftd-input-row">
                <div className="aftd-input-left">
                  <Smile className="aftd-input-icon" size={18} aria-hidden="true" />
                  <div className="aftd-input-text-wrap">
                    {displayValue.length === 0 && phase !== 'answering' ? (
                      <span className="aftd-placeholder">{placeholder}</span>
                    ) : null}
                    <span className="aftd-input-text">{displayValue}</span>
                    {phase !== 'answering' && phase !== 'complete' && <Cursor />}
                  </div>
                </div>

                <div className="aftd-actions" aria-hidden="true">
                  <SlidersHorizontal size={19} />
                  <Settings size={19} />
                  <Headphones size={19} />
                  <div className="aftd-guidance">
                    <Zap size={19} fill="currentColor" />
                    <span>{guidanceLabel}</span>
                  </div>
                  <Mic size={19} />
                  <AnimatePresence mode="wait">
                    {answering ? (
                      <motion.div
                        key="answering"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.25 }}
                      >
                        <AnsweringPill answeringText={answeringText} />
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                  <motion.div
                    className={['aftd-send', phase === 'submit' || answering ? 'aftd-send-active' : ''].join(' ')}
                    animate={phase === 'submit' ? { scale: [1, 1.1, 1] } : { scale: 1 }}
                    transition={{ duration: 0.45, repeat: phase === 'submit' ? 1 : 0 }}
                  >
                    <ArrowUp size={22} aria-hidden="true" />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
